import { reaction } from "mobx";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";

import { useStores } from "@stores";

const DEFAULT_TITLE = "Ekiden";
const TITLE_UPDATE_THROTTLE_MS = 2000; // Update title max once per 2 seconds

const PAGE_TITLES: Record<string, string> = {
  portfolio: "Portfolio",
  leaderboard: "Leaderboard",
  competitions: "Competitions",
  referral: "Referral",
};

export const TabTitleUpdater = () => {
  const { marketStore } = useStores();
  const location = useLocation();
  const [title, setTitle] = useState(DEFAULT_TITLE);
  const throttleRef = useRef(false);
  const lastPriceRef = useRef<string | null>(null);

  const isTradesPage = location.pathname.startsWith("/trades");

  useEffect(() => {
    const updateTitle = () => {
      const activeMarket = marketStore.activeMarket;

      if (isTradesPage && activeMarket) {
        const markPrice = Number(activeMarket.mark_price) || 0;
        if (markPrice === 0) {
          return `${activeMarket.base_symbol} | ${DEFAULT_TITLE}`;
        }
        // Always use 2 decimal places for title
        const formattedPrice = `$${markPrice.toFixed(2)}`;
        return `${formattedPrice} | ${activeMarket.base_symbol} | ${DEFAULT_TITLE}`;
      }

      const pathSegment = location.pathname.split("/")[1];
      const pageTitle = PAGE_TITLES[pathSegment];
      if (pageTitle) {
        return `${pageTitle} | ${DEFAULT_TITLE}`;
      }

      return DEFAULT_TITLE;
    };

    // Initial title
    setTitle(updateTitle());

    // Throttled reaction to price changes
    const dispose = reaction(
      () => ({
        markPrice: marketStore.activeMarket?.mark_price,
        symbol: marketStore.activeMarket?.base_symbol,
      }),
      (data) => {
        // Skip if price hasn't changed significantly (same rounded value)
        const currentPrice = data.markPrice ? Number(data.markPrice).toFixed(2) : null;
        if (currentPrice === lastPriceRef.current) {
          return;
        }

        if (!throttleRef.current) {
          throttleRef.current = true;
          lastPriceRef.current = currentPrice;
          setTimeout(() => {
            setTitle(updateTitle());
            throttleRef.current = false;
          }, TITLE_UPDATE_THROTTLE_MS);
        }
      },
    );

    return dispose;
  }, [marketStore, isTradesPage, location.pathname]);

  useEffect(() => {
    document.title = title;
    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, [title]);

  return null;
};
